import { cornerRadiusLayer, strokeThickness } from '@adaptive-web/adaptive-ui/reference';
import { css } from '@microsoft/fast-element';
import { darkModeQuery } from '../../style.js';
import { MatchMediaStyleSheetBehavior } from '@microsoft/fast-foundation';
import { imageHoverStyles } from '../image/styles.js';

export const expandCardHoverStyles = css.partial`
	-webkit-line-clamp: unset;
	max-block-size: 100%;
	height: calc(var(--expand-height) + 2px);
`;

export const expandCardStyles = css`
	:host {
		--card-height: 320px;
		--expand-height: var(--card-height) - var(--padding-sm);

		display: flex;
		flex-direction: column;
		contain: content;
		background-color: var(--on-surface-color);
		border-radius: ${cornerRadiusLayer};
		container-type: inline-size;
		position: relative;
		box-sizing: border-box;
		border: 1px solid transparent;
	}

	${imageHoverStyles('.image:hover::part(image-container)', '.image:hover::part(filter)')}

	.heading {
		align-self: flex-start;
		background-color: var(--primary-color);
		color: #ffffff; 
		padding: calc(var(--padding-xs) * 2);
		width: 100%;
		margin: 0;
		box-sizing: border-box;
		font-variation-settings: 'wght' 600;
		font-size: 16px;
	}

	.image {
		display: flex;
		height: var(--card-height);
		border-radius: 0;
		contain: paint;
		width: 100%;
		z-index: 0;
		box-sizing: border-box;
	}

	.body {
		z-index: 1;
		background-color: var(--surface-color);
		padding: var(--padding-sm);

		max-block-size: calc(2rlh + var(--padding-sm) + 3px);
		height: calc(2rlh + var(--padding-sm) + 3px);

		position: absolute;
		bottom: 0;
		left: 0;

		-webkit-line-clamp: 2;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		contain: content;
		transition: max-block-size 200ms ease, height 200ms ease;
		box-sizing: border-box;
		margin-bottom: var(--padding-sm);
		font-size: 14px;
		box-sizing: border-box;
	}

	:host::after {
		content: '';
		position: absolute;
		background-color: var(--surface-color);
		bottom: -1px;
		left: 0;
		width: 100%;
		height: calc(var(--padding-sm) + 1px);
		box-sizing: border-box;
		z-index: 3;
		box-sizing: border-box;
	}

	.body ::slotted(*) {
		pointer-events: none;
	}

	@media (hover: hover) and (pointer: fine) {
		.body:hover {
			${expandCardHoverStyles}
		}
	}

	@supports (overflow-clip-margin: content-box) {
		:host {
			--expand-height: 320px;
		}

		.body {
			overflow-clip-margin: content-box;
			max-block-size: calc(2rlh + (var(--padding-sm) * 2 + 3px));
			height: calc(2rlh + (var(--padding-sm) * 2 + 3px));
			margin-bottom: 0;
		}

		:host::after {
			content: unset;
			display: none;
		}
	}
`.withBehaviors(
	new MatchMediaStyleSheetBehavior(
		darkModeQuery,
		css`
			:host {
				border-color: var(--on-surface-color);
			}

			.heading {
				background-color: var(--surface-color);
			}

			.body {
				background-color: var(--on-surface-color);
			}

			:host::after {
				background-color: var(--on-surface-color);
			}
		`)
)

export const simpleCardStyles = css`
	:host {
		--card-height: 300px;

		display: grid;
		grid-template-rows: 1fr max-content;
		height: var(--card-height);
		border-radius: ${cornerRadiusLayer};
		contain: content;
		container-type: inline-size;
		border: 1px solid transparent;
	}

	${imageHoverStyles('.image:hover::part(image-container)', '.image:hover::part(filter)')}

	.image {
		grid-row: 1 / span all;
		grid-column: 1;
		height: var(--card-height);
		border-radius: 0;
		width: 100%;
		z-index: 0;
	}

	.body {
		display: flex;
		grid-row: 2;
		grid-column: 1;
		z-index: 1;
		align-self: flex-end;
		justify-content: center;
		pointer-events: none;
		padding: var(--padding-md);
		color: #ffffff;
		position: relative;
		z-index: 1;

		&::after {
			content: '';
			position: absolute;
			background-color: var(--primary-color);
			filter: opacity(0.8);
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			box-sizing: border-box;
			z-index: -1;
		}
	}

	@supports (color: rgb(from white r g b)) {
		.body {
			background-color: rgb(from var(--primary-color) r g b / 0.8);
			position: unset;

			&::after {
				content: none;
			}
		}
	}
`.withBehaviors(
	new MatchMediaStyleSheetBehavior(
		darkModeQuery,
		css`
			:host {
				border-color: var(--on-surface-color);
			}
		`
	)
)